import { useEffect, useState } from "react";
import {
  FaFileInvoiceDollar,
  FaEye,
  FaTimes,
  FaSyncAlt,
  FaPlus
} from "react-icons/fa";

const API_URL = "https://crm.parisunscripted.com/api";

export default function GuideInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [month, setMonth] = useState(getCurrentMonth());

  const token = sessionStorage.getItem("token");

  /* =========================================================
     LOAD INVOICES
  ========================================================= */

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${API_URL}/guide/invoices`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to load invoices"
        );
      }

      setInvoices(Array.isArray(data) ? data : []);

    } catch (err) {
      console.error("LOAD INVOICES ERROR:", err);
      setError(err.message);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const deleteInvoice = async (invoiceId) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this draft invoice? You can generate a new invoice for this month afterward."
  );

  if (!confirmed) {
    return;
  }

  try {
    setError("");
    setSuccess("");

    const response = await fetch(
      `${API_URL}/guide/invoices/${invoiceId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.error || "Could not delete invoice."
      );
    }

    setSelectedInvoice(null);

    setSuccess(
      "Invoice deleted successfully. You can generate a new invoice for this month."
    );

    await loadInvoices();

  } catch (err) {
    console.error("DELETE INVOICE ERROR:", err);
    setError(err.message);
  }
};

  /* =========================================================
     GENERATE INVOICE
  ========================================================= */

  const generateInvoice = async () => {
    if (!month) {
      setError("Please select a month.");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setSuccess("");

      const response = await fetch(
        `${API_URL}/guide/invoices/generate`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },

          body: JSON.stringify({
            month
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Could not generate invoice."
        );
      }

      setSuccess(
        `Invoice ${data.invoice_number} created successfully.`
      );

      await loadInvoices();

      // Automatically open newly created invoice
      if (data.invoice_id) {
        await loadInvoice(data.invoice_id);
      }

    } catch (err) {
      console.error("GENERATE INVOICE ERROR:", err);
      setError(err.message);

    } finally {
      setGenerating(false);
    }
  };

  /* =========================================================
     VIEW SINGLE INVOICE
  ========================================================= */

  const loadInvoice = async (invoiceId) => {
    try {
      setLoadingInvoice(true);
      setError("");

      const response = await fetch(
        `${API_URL}/guide/invoices/${invoiceId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to load invoice."
        );
      }

      setSelectedInvoice(data);

    } catch (err) {
      console.error("LOAD INVOICE ERROR:", err);
      setError(err.message);

    } finally {
      setLoadingInvoice(false);
    }
  };

/* =========================================================
     PDF
  ========================================================= */

  const downloadInvoice = async (invoiceId) => {
  try {
    const token =
  localStorage.getItem("token") ||
  sessionStorage.getItem("token");

    const response = await fetch(
  `${API_URL}/guide/invoices/${invoiceId}/pdf`,
  {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`
    }
  }
);

    if (!response.ok) {
      let message = "Could not download invoice";

      try {
        const data = await response.json();

        if (data.error) {
          message = data.error;
        }
      } catch {
        // Response wasn't JSON.
      }

      throw new Error(message);
    }

    /*
     * Convert PDF response into a browser Blob.
     */
    const blob = await response.blob();

    /*
     * Create temporary browser URL.
     */
    const url =
      window.URL.createObjectURL(blob);

    /*
     * Create temporary download link.
     */
    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      `invoice-${invoiceId}.pdf`;

    document.body.appendChild(link);

    link.click();

    /*
     * Clean up.
     */
    link.remove();

    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error(
      "DOWNLOAD INVOICE ERROR:",
      err
    );

    alert(
      err.message ||
      "Could not download invoice"
    );
  }
};

  /* =========================================================
     HELPERS
  ========================================================= */

  function getCurrentMonth() {
    const date = new Date();

    return `${date.getFullYear()}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}`;
  }

  const formatMonth = (date) => {
    if (!date) return "-";

    const value = new Date(date);

    return value.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric"
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "EUR"
    }).format(Number(amount || 0));
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "paid":
        return {
          background: "#dcfce7",
          color: "#166534"
        };

      case "finalized":
        return {
          background: "#dbeafe",
          color: "#1e40af"
        };

      case "draft":
      default:
        return {
          background: "#fef3c7",
          color: "#92400e"
        };
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div style={page}>

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div style={header}>

        <div>

          <div style={titleRow}>
            <div style={titleIcon}>
              <FaFileInvoiceDollar />
            </div>

            <div>
              <h1 style={title}>
                My Invoices
              </h1>

              <p style={subtitle}>
                View your completed tours and monthly earnings.
              </p>
            </div>
          </div>

        </div>

        <button
          style={refreshButton}
          onClick={loadInvoices}
          disabled={loading}
        >
          <FaSyncAlt
            style={{
              animation: loading
                ? "spin 1s linear infinite"
                : "none"
            }}
          />

          Refresh
        </button>

      </div>

      {/* =====================================================
          ALERTS
      ===================================================== */}

      {error && (
        <div style={errorBox}>
          <div>
            <strong>Something went wrong</strong>
            <div style={{ marginTop: "4px" }}>
              {error}
            </div>
          </div>

          <button
            style={alertClose}
            onClick={() => setError("")}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {success && (
        <div style={successBox}>

          <div>
            <strong>Success</strong>

            <div style={{ marginTop: "4px" }}>
              {success}
            </div>
          </div>

          <button
            style={alertClose}
            onClick={() => setSuccess("")}
          >
            <FaTimes />
          </button>

        </div>
      )}

      {/* =====================================================
          GENERATE INVOICE CARD
      ===================================================== */}

      <div style={generateCard}>

        <div style={generateInfo}>

          <div style={generateIcon}>
            <FaFileInvoiceDollar />
          </div>

          <div>

            <h2 style={generateTitle}>
              Generate Monthly Invoice
            </h2>

            <p style={generateDescription}>
              Create an invoice containing all completed
              tours for the selected month.
            </p>

          </div>

        </div>

        <div style={generateControls}>

          <div style={inputGroup}>

            <label style={label}>
              Invoice month
            </label>

            <input
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setError("");
                setSuccess("");
              }}
              style={monthInput}
            />

          </div>

          <button
            style={{
              ...generateButton,
              opacity: generating ? 0.7 : 1,
              cursor: generating
                ? "not-allowed"
                : "pointer"
            }}
            onClick={generateInvoice}
            disabled={generating}
          >

            {generating ? (
              <>
                <FaSyncAlt
                  style={{
                    animation:
                      "spin 1s linear infinite"
                  }}
                />

                Generating...
              </>
            ) : (
              <>
                <FaPlus />

                Generate Invoice
              </>
            )}

          </button>

        </div>

      </div>

      {/* =====================================================
          INVOICE LIST
      ===================================================== */}

      <div style={sectionHeader}>

        <div>
          <h2 style={sectionTitle}>
            Invoice History
          </h2>

          <p style={sectionSubtitle}>
            Your previously generated invoices.
          </p>
        </div>

        <div style={invoiceCount}>
          {invoices.length}{" "}
          {invoices.length === 1
            ? "invoice"
            : "invoices"}
        </div>

      </div>

      {loading ? (

        <div style={emptyCard}>
          <FaSyncAlt
            style={{
              fontSize: "24px",
              animation: "spin 1s linear infinite",
              marginBottom: "12px"
            }}
          />

          <p style={{ margin: 0 }}>
            Loading invoices...
          </p>
        </div>

      ) : invoices.length === 0 ? (

        <div style={emptyCard}>

          <div style={emptyIcon}>
            <FaFileInvoiceDollar />
          </div>

          <h3 style={emptyTitle}>
            No invoices yet
          </h3>

          <p style={emptyText}>
            Once you have completed tours, select a month
            above to generate your first invoice.
          </p>

        </div>

      ) : (

        <div style={tableCard}>

          <div style={tableScroll}>

            <table style={table}>

              <thead>

                <tr>

                  <th style={th}>
                    Invoice
                  </th>

                  <th style={th}>
                    Month
                  </th>

                  <th style={th}>
                    Status
                  </th>

                  <th style={th}>
                    Total
                  </th>

                  <th
                    style={{
                      ...th,
                      textAlign: "right"
                    }}
                  >
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {invoices.map((invoice) => (

                  <tr
                    key={invoice.id}
                    style={tableRow}
                  >

                    <td style={td}>

                      <div style={invoiceNumber}>
                        {invoice.invoice_number}
                      </div>

                      <div style={createdDate}>
                        Created{" "}
                        {formatDate(
                          invoice.created_at
                        )}
                      </div>

                    </td>

                    <td style={td}>
                      <strong>
                        {formatMonth(
                          invoice.invoice_month
                        )}
                      </strong>
                    </td>

                    <td style={td}>

                      <span
                        style={{
                          ...statusBadge,
                          ...getStatusStyle(
                            invoice.status
                          )
                        }}
                      >
                        {invoice.status}
                      </span>

                    </td>

                    <td style={td}>

                      <strong style={totalAmount}>
                        {formatMoney(
                          invoice.total_amount
                        )}
                      </strong>

                    </td>

                    <td
                      style={{
                        ...td,
                        textAlign: "right"
                      }}
                    >

                      <div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    gap: 8
  }}
>
  <button
    style={viewButton}
    onClick={() =>
      loadInvoice(invoice.id)
    }
  >
    <FaEye />
    View
  </button>

  {invoice.status === "draft" && (
    <button
      style={deleteButton}
      onClick={() =>
        deleteInvoice(invoice.id)
      }
    >
      🗑️ Delete
    </button>
  )}
</div>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>

      )}

      {/* =====================================================
          INVOICE MODAL
      ===================================================== */}

      {selectedInvoice && (

        <div
          style={modalOverlay}
          onClick={() =>
            setSelectedInvoice(null)
          }
        >

          <div
            style={modal}
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* MODAL HEADER */}

            <div style={modalHeader}>

              <div>

                <div style={modalInvoiceNumber}>
                  {selectedInvoice.invoice.invoice_number}
                </div>

                <h2 style={modalTitle}>
                  {formatMonth(
                    selectedInvoice.invoice.invoice_month
                  )}
                </h2>

              </div>

              <button
                style={closeButton}
                onClick={() =>
                  setSelectedInvoice(null)
                }
              >
                <FaTimes />
              </button>

            </div>

            {/* GUIDE DETAILS */}

            <div style={guideInfoCard}>

              <div style={guideInfoItem}>

                <span style={infoLabel}>
                  Guide
                </span>

                <strong style={infoValue}>
                  {
                    selectedInvoice.invoice
                      .first_name
                  }{" "}
                  {
                    selectedInvoice.invoice
                      .last_name
                  }
                </strong>

              </div>

              <div style={guideInfoItem}>

                <span style={infoLabel}>
                  Email
                </span>

                <strong style={infoValue}>
                  {
                    selectedInvoice.invoice
                      .email
                  }
                </strong>

              </div>

              <div style={guideInfoItem}>

                <span style={infoLabel}>
                  Status
                </span>

                <span
                  style={{
                    ...statusBadge,
                    ...getStatusStyle(
                      selectedInvoice.invoice.status
                    )
                  }}
                >
                  {
                    selectedInvoice.invoice.status
                  }
                </span>

              </div>

            </div>

            {/* TOUR LINES */}

            <div style={modalSectionTitle}>
              Completed Tours
            </div>

            {loadingInvoice ? (

              <div style={modalLoading}>
                <FaSyncAlt
                  style={{
                    animation:
                      "spin 1s linear infinite"
                  }}
                />

                Loading invoice...
              </div>

            ) : selectedInvoice.lines.length === 0 ? (

              <div style={modalEmpty}>
                No tours found on this invoice.
              </div>

            ) : (

              <div style={modalTableWrapper}>

                <table style={table}>

                  <thead>

                    <tr>

                      <th style={th}>
                        Date
                      </th>

                      <th style={th}>
                        Tour
                      </th>

                      <th
                        style={{
                          ...th,
                          textAlign: "right"
                        }}
                      >
                        Your rate
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {selectedInvoice.lines.map(
                      (line) => (

                        <tr
                          key={line.id}
                          style={tableRow}
                        >

                          <td style={td}>
                            {formatDateTime(
                              line.tour_date
                            )}
                          </td>

                          <td style={td}>

                            <div
                              style={{
                                fontWeight: 700
                              }}
                            >
                              {line.tour_title}
                            </div>

                            <div
                              style={createdDate}
                            >
                              Tour session #
                              {" "}
                              {line.tour_session_id}
                            </div>

                          </td>

                          <td
                            style={{
                              ...td,
                              textAlign: "right"
                            }}
                          >

                            <strong>
                              {formatMoney(
                                line.commission_amount
                              )}
                            </strong>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

            )}

            {/* TOTAL */}

            <div style={totalBox}>

              <span style={totalLabel}>
                Total invoice amount
              </span>

              <strong style={modalTotal}>
                {formatMoney(
                  selectedInvoice.invoice
                    .total_amount
                )}
              </strong>

            </div>

            {/* FOOTER */}

            <div style={modalFooter}>

            <span style={createdText}>
              Created{" "}
              {formatDateTime(
                selectedInvoice.invoice.created_at
              )}
            </span>

            <div style={modalActions}>

              <button
                style={closeModalButton}
                onClick={() =>
                  setSelectedInvoice(null)
                }
              >
                Close
              </button>

              <button
                onClick={() =>
                  downloadInvoice(
                    selectedInvoice.invoice.id
                  )
                }
                style={downloadButton}
              >
                Download PDF
              </button>

            </div>

          </div>

        </div>
        
        </div>

      )}

      {/* =====================================================
          ANIMATIONS
      ===================================================== */}

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>

    </div>
  );
}

/* ============================================================
   STYLES
============================================================ */

const page = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto"
};

const header = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "20px",
  marginBottom: "30px"
};

const titleRow = {
  display: "flex",
  alignItems: "center",
  gap: "15px"
};

const titleIcon = {
  width: "52px",
  height: "52px",
  borderRadius: "14px",
  background: "#fff1e7",
  color: "#c75028",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "24px"
};

const title = {
  margin: 0,
  fontSize: "30px",
  fontWeight: "800",
  color: "#111827"
};

const subtitle = {
  margin: "5px 0 0",
  color: "#6b7280",
  fontSize: "15px"
};

const refreshButton = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 15px",
  border: "1px solid #e5e7eb",
  borderRadius: "9px",
  background: "#ffffff",
  color: "#374151",
  cursor: "pointer",
  fontWeight: "600"
};

const generateCard = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "30px",
  padding: "22px",
  marginBottom: "35px",
  borderRadius: "14px",
  border: "1px solid #eadfd8",
  background: "#fffaf7",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
};

const generateInfo = {
  display: "flex",
  alignItems: "center",
  gap: "15px"
};

const generateIcon = {
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  background: "#c75028",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "21px"
};

const generateTitle = {
  margin: 0,
  fontSize: "18px",
  color: "#111827"
};

const generateDescription = {
  margin: "5px 0 0",
  color: "#6b7280",
  fontSize: "14px"
};

const generateControls = {
  display: "flex",
  alignItems: "flex-end",
  gap: "12px"
};

const inputGroup = {
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};

const label = {
  fontSize: "12px",
  fontWeight: "700",
  color: "#374151"
};

const monthInput = {
  height: "42px",
  padding: "0 12px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  background: "white",
  fontSize: "14px"
};

const generateButton = {
  height: "42px",
  padding: "0 17px",
  border: "none",
  borderRadius: "8px",
  background: "#c75028",
  color: "white",
  fontWeight: "700",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px"
};

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "15px"
};

const sectionTitle = {
  margin: 0,
  fontSize: "21px",
  color: "#111827"
};

const sectionSubtitle = {
  margin: "4px 0 0",
  color: "#6b7280",
  fontSize: "14px"
};

const invoiceCount = {
  padding: "6px 12px",
  borderRadius: "20px",
  background: "#f3f4f6",
  color: "#374151",
  fontSize: "13px",
  fontWeight: "700"
};

const tableCard = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
};

const tableScroll = {
  width: "100%",
  overflowX: "auto"
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "700px"
};

const th = {
  padding: "14px 18px",
  background: "#f9fafb",
  borderBottom: "1px solid #e5e7eb",
  color: "#6b7280",
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  textAlign: "left"
};

const td = {
  padding: "16px 18px",
  borderBottom: "1px solid #f1f1f1",
  color: "#1f2937",
  fontSize: "14px"
};

const tableRow = {
  transition: "background 0.15s"
};

const invoiceNumber = {
  fontWeight: "800",
  color: "#111827"
};

const createdDate = {
  marginTop: "4px",
  color: "#9ca3af",
  fontSize: "12px"
};

const statusBadge = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 10px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "capitalize"
};

const totalAmount = {
  fontSize: "15px",
  color: "#111827"
};

const viewButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "8px 12px",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  background: "white",
  color: "#374151",
  cursor: "pointer",
  fontWeight: "700"
};

const emptyCard = {
  minHeight: "250px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  color: "#6b7280"
};

const emptyIcon = {
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  background: "#fff1e7",
  color: "#c75028",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "25px",
  marginBottom: "15px"
};

const emptyTitle = {
  margin: 0,
  color: "#111827"
};

const emptyText = {
  maxWidth: "450px",
  textAlign: "center",
  lineHeight: 1.5,
  marginTop: "8px"
};

const errorBox = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "14px 16px",
  marginBottom: "20px",
  borderRadius: "10px",
  background: "#fee2e2",
  color: "#991b1b",
  border: "1px solid #fecaca"
};

const successBox = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "14px 16px",
  marginBottom: "20px",
  borderRadius: "10px",
  background: "#dcfce7",
  color: "#166534",
  border: "1px solid #bbf7d0"
};

const alertClose = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "inherit",
  fontSize: "15px"
};

const modalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  zIndex: 9999
};

const modal = {
  width: "100%",
  maxWidth: "900px",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  padding: "25px"
};

const modalHeader = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: "25px"
};

const modalInvoiceNumber = {
  color: "#c75028",
  fontSize: "13px",
  fontWeight: "800",
  marginBottom: "4px"
};

const modalTitle = {
  margin: 0,
  fontSize: "25px",
  color: "#111827"
};

const closeButton = {
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  border: "none",
  background: "#f3f4f6",
  color: "#374151",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
};

const guideInfoCard = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "15px",
  padding: "18px",
  background: "#f9fafb",
  borderRadius: "12px",
  marginBottom: "25px"
};

const guideInfoItem = {
  display: "flex",
  flexDirection: "column",
  gap: "5px"
};

const infoLabel = {
  color: "#9ca3af",
  fontSize: "11px",
  fontWeight: "800",
  textTransform: "uppercase"
};

const infoValue = {
  color: "#111827",
  fontSize: "14px"
};

const modalSectionTitle = {
  fontSize: "16px",
  fontWeight: "800",
  color: "#111827",
  marginBottom: "12px"
};

const modalTableWrapper = {
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  overflow: "hidden"
};

const modalLoading = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  padding: "40px",
  color: "#6b7280"
};

const modalEmpty = {
  padding: "30px",
  textAlign: "center",
  color: "#6b7280",
  background: "#f9fafb",
  borderRadius: "10px"
};

const totalBox = {
  marginTop: "20px",
  padding: "18px",
  borderRadius: "10px",
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
};

const totalLabel = {
  color: "#7c2d12",
  fontWeight: "700"
};

const modalTotal = {
  fontSize: "24px",
  color: "#c75028"
};

const modalFooter = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "20px"
};

const createdText = {
  color: "#9ca3af",
  fontSize: "12px"
};

const closeModalButton = {
  padding: "9px 18px",
  borderRadius: "8px",
  border: "none",
  background: "#111827",
  color: "white",
  cursor: "pointer",
  fontWeight: "700"
};

const downloadButton = {
  padding: "9px 16px",
  border: "none",
  borderRadius: "8px",
  background: "#c75028",
  color: "white",
  fontWeight: "700",
  cursor: "pointer"
};

const modalActions = {
  display: "flex",
  alignItems: "center",
  gap: "10px"
};

const deleteButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  border: "1px solid #fecaca",
  borderRadius: 8,
  background: "#fef2f2",
  color: "#dc2626",
  cursor: "pointer",
  fontWeight: 600
};